import { useCallback, useRef, useState } from 'react'

import { AvailabilityMap, LessonDto } from '../../../service/types'
import { isRangeAvailable } from './availability'

function addMinutesToTime(time: string, minutes: number): string {
  const [h, m] = time.split(':').map(Number)
  const total = h * 60 + m + minutes
  return `${Math.floor(total / 60)
    .toString()
    .padStart(2, '0')}:${(total % 60).toString().padStart(2, '0')}`
}

function computeDropEndTime(lesson: LessonDto, dropTime: string): string {
  const [sh, sm] = lesson.startTime.split(':').map(Number)
  const [eh, em] = lesson.endTime.split(':').map(Number)
  const durationMin = eh * 60 + em - (sh * 60 + sm)
  return addMinutesToTime(dropTime, durationMin)
}

export type DropTarget = { instructorId: string | null; time: string }

export type DragState =
  | { phase: 'idle' }
  | {
      phase: 'pending'
      lesson: LessonDto
      sourceInstructorId: string | null
      startX: number
      startY: number
      offsetX: number
      offsetY: number
      width: number
      height: number
    }
  | {
      phase: 'dragging'
      lesson: LessonDto
      sourceInstructorId: string | null
      ghostX: number
      ghostY: number
      offsetX: number
      offsetY: number
      width: number
      height: number
      dropTarget: DropTarget | null
    }

const DRAG_THRESHOLD = 5
// Require the card's left edge to be this many pixels past a slot boundary before snapping to the next slot
const DROP_X_TOLERANCE = -8

function findDropTarget(x: number, y: number): DropTarget | null {
  // Find the instructor row by looking for an element with data-instructor-id
  const elements = document.elementsFromPoint(x, y)
  let instructorId: string | null = null
  let found = false
  for (const el of elements) {
    const row = el.closest<HTMLElement>('[data-instructor-id]')
    if (row) {
      const id = row.dataset.instructorId!
      instructorId = id === 'unassigned' ? null : id
      found = true
      break
    }
  }
  if (!found) return null

  // Find the time slot by matching X position against the time header cells
  const headerCells = document.querySelectorAll<HTMLElement>('[data-time-header]')
  for (const headerCell of headerCells) {
    const rect = headerCell.getBoundingClientRect()
    if (x >= rect.left && x < rect.right) {
      const time = headerCell.dataset.timeHeader
      if (time) {
        return { instructorId, time }
      }
    }
  }
  return null
}

export type OnLessonDrop = (
  lessonId: string,
  sourceInstructorId: string | null,
  targetInstructorId: string | null,
  time: string,
) => void

export function useLessonDrag(onDrop: OnLessonDrop, availabilityMap: AvailabilityMap) {
  const [dragState, setDragState] = useState<DragState>({ phase: 'idle' })
  const stateRef = useRef<DragState>(dragState)
  const skipClickRef = useRef(false)
  const onDropRef = useRef(onDrop)
  onDropRef.current = onDrop

  const updateState = useCallback((newState: DragState) => {
    stateRef.current = newState
    setDragState(newState)
  }, [])

  const handlePointerDown = useCallback(
    (e: React.PointerEvent, lesson: LessonDto, sourceInstructorId: string | null) => {
      if (e.button !== 0) return
      e.preventDefault()
      const target = e.currentTarget as HTMLElement
      target.setPointerCapture(e.pointerId)
      const rect = target.getBoundingClientRect()
      const state: DragState = {
        phase: 'pending',
        lesson,
        sourceInstructorId,
        startX: e.clientX,
        startY: e.clientY,
        offsetX: e.clientX - rect.left,
        offsetY: e.clientY - rect.top,
        width: rect.width,
        height: rect.height,
      }
      stateRef.current = state
      setDragState(state)
    },
    [],
  )

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      const current = stateRef.current
      if (current.phase === 'pending') {
        const dx = e.clientX - current.startX
        const dy = e.clientY - current.startY
        const distance = Math.sqrt(dx * dx + dy * dy)
        if (distance > DRAG_THRESHOLD) {
          const dropTarget = findDropTarget(e.clientX - current.offsetX - DROP_X_TOLERANCE, e.clientY)
          const validTarget =
            dropTarget && dropTarget.instructorId
              ? isRangeAvailable(
                  dropTarget.instructorId,
                  dropTarget.time,
                  computeDropEndTime(current.lesson, dropTarget.time),
                  availabilityMap,
                )
                ? dropTarget
                : null
              : dropTarget
          updateState({
            phase: 'dragging',
            lesson: current.lesson,
            sourceInstructorId: current.sourceInstructorId,
            ghostX: e.clientX,
            ghostY: e.clientY,
            offsetX: current.offsetX,
            offsetY: current.offsetY,
            width: current.width,
            height: current.height,
            dropTarget: validTarget,
          })
        }
      } else if (current.phase === 'dragging') {
        const dropTarget = findDropTarget(e.clientX - current.offsetX - DROP_X_TOLERANCE, e.clientY)
        const validTarget =
          dropTarget && dropTarget.instructorId
            ? isRangeAvailable(
                dropTarget.instructorId,
                dropTarget.time,
                computeDropEndTime(current.lesson, dropTarget.time),
                availabilityMap,
              )
              ? dropTarget
              : null
            : dropTarget
        updateState({
          phase: 'dragging',
          lesson: current.lesson,
          sourceInstructorId: current.sourceInstructorId,
          ghostX: e.clientX,
          ghostY: e.clientY,
          offsetX: current.offsetX,
          offsetY: current.offsetY,
          width: current.width,
          height: current.height,
          dropTarget: validTarget,
        })
      }
    },
    [updateState, availabilityMap],
  )

  const handlePointerUp = useCallback(
    (_e: React.PointerEvent) => {
      const current = stateRef.current
      if (current.phase === 'dragging' && current.dropTarget) {
        const { lesson, sourceInstructorId, dropTarget } = current
        const isDifferent = dropTarget.instructorId !== sourceInstructorId || dropTarget.time !== lesson.startTime
        if (isDifferent) {
          onDropRef.current(lesson.id, sourceInstructorId, dropTarget.instructorId, dropTarget.time)
        }
      }
      if (current.phase === 'dragging') {
        skipClickRef.current = true
        requestAnimationFrame(() => {
          skipClickRef.current = false
        })
      }
      updateState({ phase: 'idle' })
    },
    [updateState],
  )

  return {
    dragState,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    skipClickRef,
  }
}
